// admin/src/pages/ScimIntegrations/scimApi.js

import { request } from '../../utils/request';

const SCIM_BASE_PATH = 'admin/scim';

const DEFAULT_FETCH_CONFIG = {
  cache: 'no-store',
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
};

/**
 * Normalizes backend error objects into client-safe human-friendly messages.
 * Maps:
 * - 401 => Session expired / unauthorized
 * - 403 => Permission denied
 * - 404 (SCIM_ENABLED off) => 'SCIM is disabled on the server'
 * - 503 (invalid SCIM config) => 'SCIM configuration error, contact an administrator'
 * - 409 (token limit) => backend's exact message (e.g. max active credentials)
 *
 * @param {any} err
 * @returns {string}
 */
export function extractScimErrorMessage(err) {
  if (!err) return 'An unexpected error occurred.';

  const status = err.status || err.statusCode || err.response?.status;
  const msg = String(err.message || err.error || err.response?.data?.message || err.response?.data?.error || '');

  if (status === 401 || msg.toLowerCase().includes('unauthorized') || msg.toLowerCase().includes('authentication required')) {
    return 'Your session has expired. Please log in again.';
  }

  if (
    status === 403 ||
    msg.toLowerCase().includes('missing required permission') ||
    msg.toLowerCase().includes('access denied') ||
    msg.toLowerCase().includes('forbidden')
  ) {
    return 'Access denied. You do not have permission to perform this action.';
  }

  if (status === 404 || msg.toLowerCase().includes('disabled') || msg.toLowerCase().includes('scim integrations are currently disabled')) {
    return 'SCIM is disabled on the server';
  }

  if (
    status === 503 ||
    msg.toLowerCase().includes('unavailable') ||
    msg.toLowerCase().includes('scim service is currently unavailable')
  ) {
    return 'SCIM configuration error, contact an administrator';
  }

  if (status === 409 || msg.toLowerCase().includes('maximum') || msg.toLowerCase().includes('active credentials')) {
    return msg || 'Connection already has the maximum of 2 active credentials. Please rotate or revoke an existing token.';
  }

  return msg || 'Operation failed. Please try again.';
}

/**
 * List paginated SCIM connections
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.status]
 * @param {AbortSignal} [signal]
 */
export async function listConnections({ page = 1, limit = 10, status } = {}, signal) {
  const query = { page, limit };
  if (status) query.status = status;

  return request(`${SCIM_BASE_PATH}/connections`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Create a new SCIM connection and retrieve initial one-time plaintext token
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} [payload.providerType]
 * @param {Object} [payload.config]
 * @param {Array} [payload.attributeMapping]
 */
export async function createConnection(payload) {
  return request(`${SCIM_BASE_PATH}/connections`, {
    method: 'POST',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get single connection detail with credentials list
 * @param {string} connectionId
 * @param {AbortSignal} [signal]
 */
export async function getConnectionById(connectionId, signal) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Update connection settings
 * @param {string} connectionId
 * @param {Object} payload
 */
export async function updateConnection(connectionId, payload) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}`, {
    method: 'PUT',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Update connection status
 * @param {string} connectionId
 * @param {'active'|'disabled'|'suspended'} status
 */
export async function updateConnectionStatus(connectionId, status) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/status`, {
    method: 'PATCH',
    body: { status },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Enable connection
 * @param {string} connectionId
 */
export async function enableConnection(connectionId) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/enable`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Disable connection
 * @param {string} connectionId
 */
export async function disableConnection(connectionId) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/disable`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Delete connection and revoke all active credentials
 * @param {string} connectionId
 */
export async function deleteConnection(connectionId) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}`, {
    method: 'DELETE',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get operational status and metrics for a connection
 * @param {string} connectionId
 * @param {AbortSignal} [signal]
 */
export async function getConnectionStatus(connectionId, signal) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/status`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Issue secondary bearer token for a connection
 * @param {string} connectionId
 */
export async function issueToken(connectionId) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/tokens`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Rotate bearer token with option to revoke oldest active credential
 * @param {string} connectionId
 * @param {Object} [options]
 * @param {boolean} [options.revokeOldest=true]
 * @param {string} [options.revokeTokenId]
 */
export async function rotateToken(connectionId, options = { revokeOldest: true }) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/rotate-token`, {
    method: 'POST',
    body: options,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Revoke specific token credential
 * @param {string} connectionId
 * @param {Object} payload
 * @param {string} payload.tokenId
 * @param {string} payload.reason
 */
export async function revokeToken(connectionId, payload) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/revoke-token`, {
    method: 'POST',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List audit activity logs for a connection
 * @param {string} connectionId
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @param {string} [params.outcome]
 * @param {string} [params.method]
 * @param {AbortSignal} [signal]
 */
export async function listActivities(connectionId, { page = 1, limit = 20, outcome, method } = {}, signal) {
  const query = { page, limit };
  if (outcome && outcome !== 'all') query.outcome = outcome;
  if (method && method !== 'all') query.method = method;

  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/activities`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List sync conflicts for a connection
 * @param {string} connectionId
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @param {string} [params.resolutionStatus]
 * @param {AbortSignal} [signal]
 */
export async function listConflicts(connectionId, { page = 1, limit = 20, resolutionStatus } = {}, signal) {
  const query = { page, limit };
  if (resolutionStatus && resolutionStatus !== 'all') query.resolutionStatus = resolutionStatus;

  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/conflicts`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Resolve or ignore a conflict record
 * @param {string} connectionId
 * @param {string} conflictId
 * @param {Object} payload
 * @param {'pending'|'ignored'|'resolved'} payload.resolutionStatus
 * @param {string} [payload.resolutionAction]
 * @param {string} [payload.resolutionNote]
 */
export async function resolveConflict(connectionId, conflictId, payload) {
  return request(`${SCIM_BASE_PATH}/connections/${connectionId}/conflicts/${conflictId}`, {
    method: 'PATCH',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}
